import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ShieldAlert } from "lucide-react";
import { displayClarityOnePhone } from "@shared/clarityOneCustomerAccess";
import { PaymentWelcomeStatus } from "./PaymentWelcomeStatus";

type Props = {
  customerId: number;
};

function formatWhen(value: Date | string | null | undefined): string {
  if (!value) return "Never";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "Never";
  return date.toLocaleString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function uniquenessLabel(value: string): string {
  switch (value) {
    case "unique":
      return "Unique";
    case "ambiguous":
      return "Ambiguous";
    case "conflicting":
      return "Conflicting";
    case "missing":
      return "Missing";
    case "invalid":
      return "Invalid";
    case "alias_only":
      return "Alias only";
    default:
      return value;
  }
}

export default function ClarityOneCustomerAccessReadiness({ customerId }: Props) {
  const { data, isLoading, error } = trpc.warranties.getCustomerAccessReadiness.useQuery(
    { customerId },
    { enabled: customerId > 0, refetchOnWindowFocus: true },
  );

  return (
    <Card data-testid="clarity-one-customer-access-readiness">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          Customer access readiness
        </CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Staff diagnostics only. Staff impersonation does not count as genuine customer activation.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading access readiness…
          </div>
        ) : null}
        {error ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {error.message || "Unable to load customer access readiness."}
          </p>
        ) : null}
        {data ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={data.blockerCode === "none" ? "default" : "secondary"}>
                {data.blockerCode === "none" ? "Ready" : data.blockerCode.replaceAll("_", " ")}
              </Badge>
              <Badge variant={data.otpEligible ? "default" : "outline"}>
                OTP {data.otpEligible ? "eligible" : "not eligible"}
              </Badge>
              <Badge variant={data.clarityOneEnabled ? "default" : "outline"}>
                Clarity1 {data.clarityOneEnabled ? "enabled" : "disabled"}
              </Badge>
            </div>

            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <span className="font-medium">Blocker: </span>
              {data.blockerSummary}
            </p>
            <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-950">
              <span className="font-medium">Recommended action: </span>
              {data.recommendedStaffAction}
            </p>

            <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Canonical customer</dt>
                <dd className="font-medium">
                  #{data.canonicalCustomerId}
                  {data.isCanonical ? " (this record)" : " (different record)"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Merged / hidden duplicate</dt>
                <dd className="font-medium">
                  {yesNo(data.isMergedDuplicate)} / {yesNo(data.isHiddenFromList)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Registered phone</dt>
                <dd className="font-medium">
                  {data.registeredPhonePresent
                    ? displayClarityOnePhone(data.phoneNormalized) ?? data.registeredPhoneRaw ?? "Present"
                    : "Missing"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Normalised phone</dt>
                <dd className="font-medium">
                  {data.phoneNormalizedValid ? "Valid" : "Invalid"}
                  {data.phoneNormalized ? ` (${data.phoneNormalized})` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Phone uniqueness</dt>
                <dd className="font-medium">
                  {uniquenessLabel(data.phoneUniqueness)}
                  {data.conflictingCustomerIds.length > 0
                    ? ` — other ids: ${data.conflictingCustomerIds.join(", ")}`
                    : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Customer warranty items</dt>
                <dd className="font-medium">{data.customerWarrantyItemCount}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last genuine customer login</dt>
                <dd className="font-medium">{formatWhen(data.lastGenuineCustomerLoginAt)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last staff impersonation</dt>
                <dd className="font-medium">{formatWhen(data.lastStaffImpersonationAt)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Latest OTP request</dt>
                <dd className="font-medium">
                  {data.latestOtpRequestResult ?? "None"}
                  {data.latestOtpRequestAt ? ` · ${formatWhen(data.latestOtpRequestAt)}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Latest OTP delivery</dt>
                <dd className="font-medium">
                  {data.latestOtpDeliveryResult ?? "None"}
                  {data.latestOtpDeliveryAt ? ` · ${formatWhen(data.latestOtpDeliveryAt)}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Latest OTP verification</dt>
                <dd className="font-medium">
                  {data.latestOtpVerificationResult ?? "None"}
                  {data.latestOtpVerificationAt ? ` · ${formatWhen(data.latestOtpVerificationAt)}` : ""}
                </dd>
              </div>
            </dl>
          </>
        ) : null}
        <PaymentWelcomeStatus customerId={customerId} />
      </CardContent>
    </Card>
  );
}
